import { supabase } from "../supabaseClient";

const getActiveTenantId = () => {
  const tenantId = window.localStorage.getItem('activeTenantId');
  return tenantId ? String(tenantId).trim() : '';
};

function requireActiveTenantId() {
  const tenantId = getActiveTenantId();
  if (!tenantId) {
    throw new Error('No active tenant selected.');
  }
  return tenantId;
}

export const TASK_STATUSES = [
  "Open",
  "In Progress",
  "Ready For Review",
  "In Review",
  "Amendments Required",
  "Ready For Signoff",
  "Completed",
  "Cancelled",
];

function formatOwner(profile) {
  if (!profile) return null;
  if (profile.display_name && profile.display_name.trim()) return profile.display_name.trim();
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim();
  return fullName || null;
}

export async function listTasks(projectId = null) {
  const activeTenantId = requireActiveTenantId();

  const query = supabase
    .from("tasks")
    .select(
      `
        id,
        project_id,
        title,
        description,
        task_type,
        status,
        priority,
        due_date,
        created_at,
        updated_at,
        assigned_to,
        reviewer_id,
        qa_required,
        review_stage,
        amendment_cycle_count,
        submitted_for_review_at,
        signed_off_at,
        assignee:profiles!tasks_assigned_to_fkey(display_name, first_name, last_name),
        reviewer:profiles!tasks_reviewer_id_fkey(display_name, first_name, last_name),
        task_links(entity_type, entity_id),
        projects!inner(id, name)
      `
    )
    .eq("tenant_id", activeTenantId)
    .order("updated_at", { ascending: false });

  if (projectId) {
    query.eq("project_id", projectId);
  }

  const { data, error } = await query;

  if (error) throw error;

  return (data || []).map((row) => ({
    id: row.id,
    projectId: row.project_id,
    projectName: row.projects?.name || 'Unknown Project',
    title: row.title,
    description: row.description,
    taskType: row.task_type,
    status: row.status,
    priority: row.priority,
    dueDate: row.due_date,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    assignedTo: row.assigned_to,
    reviewerId: row.reviewer_id,
    owners: formatOwner(row.assignee) ? [formatOwner(row.assignee)] : [],
    reviewerName: formatOwner(row.reviewer),
    qaRequired: Boolean(row.qa_required),
    reviewStage: row.review_stage || null,
    amendmentCycleCount: Number(row.amendment_cycle_count || 0),
    submittedForReviewAt: row.submitted_for_review_at || null,
    signedOffAt: row.signed_off_at || null,
    links: row.task_links || [],
  }));
}

export async function createTask({
  projectId,
  title,
  description = null,
  taskType = "General",
  priority = "Medium",
  assignedTo = null,
  dueDate = null,
  qaRequired = false,
  reviewerId = null,
  reviewStage = "Designer Phase",
  outcomeType = null,
  linkRecords = [],
  createdBy,
}) {
  const normalizedReviewStage = typeof reviewStage === 'string' && reviewStage.trim()
    ? reviewStage.trim()
    : 'Designer Phase';

  const activeTenantId = getActiveTenantId();
  if (!activeTenantId) {
    throw new Error('No active tenant selected.');
  }

  const payload = {
    project_id: projectId,
    title,
    description,
    task_type: taskType,
    priority,
    assigned_to: assignedTo,
    due_date: dueDate,
    qa_required: qaRequired,
    reviewer_id: reviewerId,
    review_stage: normalizedReviewStage,
    outcome_type: outcomeType,
    created_by: createdBy,
    tenant_id: activeTenantId,
  };

  const { data: insertedTask, error: taskError } = await supabase
    .from("tasks")
    .insert(payload)
    .select("id, project_id, title")
    .single();

  if (taskError) throw taskError;

  if (Array.isArray(linkRecords) && linkRecords.length > 0) {
    const activeTenantId = getActiveTenantId();
    if (!activeTenantId) {
      throw new Error('No active tenant selected.');
    }

    const linksPayload = linkRecords
      .filter((record) => record?.entityType && record?.entityId)
      .map((record) => ({
        task_id: insertedTask.id,
        entity_type: record.entityType,
        entity_id: record.entityId,
        tenant_id: activeTenantId,
      }));

    if (linksPayload.length > 0) {
      const { error: linksError } = await supabase.from("task_links").insert(linksPayload);
      if (linksError) throw linksError;
    }
  }

  return insertedTask;
}

export async function listProjectMembers(projectId) {
  if (!projectId) return [];

  const activeTenantId = requireActiveTenantId();

  const { data, error } = await supabase
    .from("project_access")
    .select("user_id, profiles:profiles(id, display_name, first_name, last_name)")
    .eq("project_id", projectId)
    .eq("tenant_id", activeTenantId);

  if (error) throw error;

  const seen = new Set();
  const members = [];

  (data || []).forEach((row) => {
    const profile = row?.profiles;
    const userId = row?.user_id || profile?.id;
    if (!userId || seen.has(userId)) return;
    seen.add(userId);

    const displayName = formatOwner(profile) || "Unknown User";
    members.push({ id: userId, name: displayName });
  });

  members.sort((a, b) => a.name.localeCompare(b.name));
  return members;
}

export async function updateTask(taskId, updates) {
  if (!taskId) throw new Error('Task ID is required');

  const payload = {};
  
  if (updates.title !== undefined) payload.title = updates.title;
  if (updates.description !== undefined) payload.description = updates.description;
  if (updates.status !== undefined) payload.status = updates.status;
  if (updates.priority !== undefined) payload.priority = updates.priority;
  if (updates.assignedTo !== undefined) payload.assigned_to = updates.assignedTo;
  if (updates.reviewerId !== undefined) payload.reviewer_id = updates.reviewerId;
  if (updates.dueDate !== undefined) payload.due_date = updates.dueDate;
  if (updates.reviewStage !== undefined) payload.review_stage = updates.reviewStage;
  if (updates.submittedForReviewAt !== undefined) payload.submitted_for_review_at = updates.submittedForReviewAt;
  if (updates.signedOffAt !== undefined) payload.signed_off_at = updates.signedOffAt;

  const { data, error } = await supabase
    .from("tasks")
    .update(payload)
    .eq("id", taskId)
    .select()
    .single();

  if (error) throw error;

  return data;
}

export async function deleteTask(taskId) {
  if (!taskId) throw new Error('Task ID is required');

  // Check for foreign key dependencies before deletion
  // This provides an additional guardrail beyond database constraints
  const { data: task, error: fetchError } = await supabase
    .from("tasks")
    .select("id, title, status")
    .eq("id", taskId)
    .single();

  if (fetchError) throw fetchError;
  if (!task) throw new Error('Task not found');

  // Prevent deletion of completed tasks as a business rule guardrail
  if (task.status === 'Completed') {
    throw new Error('Cannot delete completed tasks. Please archive instead.');
  }

  // Delete the task - database CASCADE will handle:
  // - task_links (CASCADE)
  // - qa_review_checklists (CASCADE)
  // - assigned_to will be set to NULL (SET NULL)
  const { error: deleteError } = await supabase
    .from("tasks")
    .delete()
    .eq("id", taskId);

  if (deleteError) throw deleteError;

  return task;
}